import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import {
  Archive,
  ArchiveRestore,
  GripVertical,
  Loader2,
  MapPin,
  MoreHorizontal,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Button } from "@/client/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/client/components/ui/dropdown-menu";
import { Textarea } from "@/client/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/client/components/ui/tooltip";
import { sessionUser, type Fragment } from "@/client/db";
import { formatRelativeTime, personName, usePeople } from "@/client/hooks/use-document";
import { useFragmentPlacements, useRequestPlacements } from "@/client/hooks/use-placement-suggestions";
import { useStoryData } from "@/client/hooks/use-story-data";
import { targetLabel } from "@/client/hooks/use-story-outline";
import { useTabActions } from "@/client/hooks/use-tabs-context";
import { deleteFragmentWithUndo, updateFragmentWithUndo } from "@/client/lib/undo-entries";
import { cn } from "@/client/lib/utils";
import { useFragmentDraggable } from "./fragment-dnd";
import { RoleIcon } from "./fragment-role";
import { PlaceFragmentPopover } from "./place-fragment-popover";
import { PlacementSummary } from "./placement-suggestions";

interface FragmentCardProps {
  storyId: string;
  fragment: Fragment;
  className?: string;
}

export function FragmentCard({ storyId, fragment, className }: FragmentCardProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(fragment.content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { outline, fragments: { linksByFragment } } = useStoryData();
  const people = usePeople();
  const { openSceneTab, openChapterTab } = useTabActions();
  const { placements, isPending } = useFragmentPlacements(fragment.id);
  const requestPlacements = useRequestPlacements(storyId);
  const drag = useFragmentDraggable(fragment.id, editing);

  const links = linksByFragment.get(fragment.id) ?? [];
  const archived = fragment.archivedAt !== null;
  const author = personName(people, fragment.createdByUserId, sessionUser().id);

  useEffect(() => {
    if (!editing) setDraft(fragment.content);
  }, [fragment.content, editing]);

  useEffect(() => {
    if (!editing) return;
    const el = textareaRef.current;
    el?.focus();
    el?.setSelectionRange(el.value.length, el.value.length);
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const content = draft.trim();
    if (!content || content === fragment.content) {
      setDraft(fragment.content);
      return;
    }
    // A failed save is reported; reopen the editor with the text so nothing is lost
    updateFragmentWithUndo(fragment.id, { content }).catch(() => {
      setDraft(content);
      setEditing(true);
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setDraft(fragment.content);
      setEditing(false);
    } else if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      commit();
    }
  };

  return (
    <div
      ref={drag.setNodeRef}
      data-region="fragment"
      className={cn(
        "group relative rounded-md border bg-card px-3 py-2 shadow-xs transition-colors",
        drag.isDragging && "opacity-40",
        archived && "opacity-70",
        className
      )}
    >
      {!editing && (
        <button
          type="button"
          className="absolute -left-0.5 top-2 flex h-5 w-4 cursor-grab items-center justify-center rounded text-muted-foreground/60 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 active:cursor-grabbing"
          aria-label="Drag onto a scene or chapter"
          {...drag.listeners}
          {...drag.attributes}
        >
          <GripVertical className="size-3.5" />
        </button>
      )}

      {editing ? (
        <Textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          className="min-h-20 resize-none text-sm"
          aria-label="Edit idea"
        />
      ) : (
        <button
          type="button"
          className="block w-full cursor-text whitespace-pre-wrap break-words text-left text-sm leading-relaxed line-clamp-[8]"
          onClick={() => setEditing(true)}
          title="Click to edit"
        >
          {fragment.content}
        </button>
      )}

      {links.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {links.map((link) => {
            const label = targetLabel(outline, link);
            return (
              <button
                key={link.id}
                type="button"
                onClick={() =>
                  link.sceneId ? openSceneTab(link.sceneId, label.title) : openChapterTab(link.chapterId!, label.title)
                }
                className="inline-flex max-w-full items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                title={label.context ? `${label.context} · ${label.title}` : label.title}
              >
                <RoleIcon role={link.role} />
                <span className="truncate">{label.title}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-1.5 flex items-center gap-1">
        <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
          {author ? `${author} · ` : ""}
          {formatRelativeTime(fragment.createdAt)}
        </span>
        <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 has-[[data-state=open]]:opacity-100 [@media(hover:none)]:opacity-100">
          {!archived && (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 gap-1 px-1.5 text-[11px]"
                disabled={isPending}
                onClick={() => requestPlacements([fragment.id])}
              >
                {isPending ? <Loader2 className="size-3 animate-spin" /> : <Sparkles className="size-3 text-primary" />}
                Suggest where it goes
              </Button>
              <Tooltip>
                <PlaceFragmentPopover storyId={storyId} fragmentId={fragment.id}>
                  <TooltipTrigger asChild>
                    <Button size="icon" variant="ghost" className="size-6" aria-label="Put it in a scene or chapter">
                      <MapPin className="size-3.5" />
                    </Button>
                  </TooltipTrigger>
                </PlaceFragmentPopover>
                <TooltipContent>Put it in a scene or chapter</TooltipContent>
              </Tooltip>
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="size-6" aria-label="More">
                <MoreHorizontal className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => updateFragmentWithUndo(fragment.id, { archived: !archived }).catch(() => {})}>
                {archived ? <ArchiveRestore /> : <Archive />}
                {archived ? "Restore" : "Archive"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={() => deleteFragmentWithUndo(storyId, fragment.id).catch(() => {})}>
                <Trash2 />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {isPending && placements.length === 0 && (
        <p className="mt-2 flex items-center gap-1.5 border-t pt-2 text-xs text-muted-foreground">
          <Loader2 className="size-3 animate-spin" />
          Looking for a spot…
        </p>
      )}
      <PlacementSummary fragmentId={fragment.id} placements={placements} />
    </div>
  );
}
